import { useEffect, useState } from 'react'
import { useParams, useLocation, NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  FiArrowLeft, FiUsers, FiClock, FiBarChart2, FiShield, FiBriefcase,
  FiMapPin, FiCalendar, FiLayers, FiAlertCircle
} from 'react-icons/fi'
import { EmpresaGestionProvider } from '../../contexts/EmpresaGestionContext'
import { asesoriaAPI } from '../../services/api'
import { Btn, Pill } from '../../ui'
import toast from 'react-hot-toast'

/*
 * Sub-shell para que un admin de asesoría gestione una empresa cliente.
 * Mismo patrón que GestionEmpresaAdmin pero con tono "asesoría" (sin gradient morado).
 */

const GestionEmpresaAsesoriaV2 = () => {
  const { empresaId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [empresa, setEmpresa] = useState(location.state?.empresa || null)
  const [loading, setLoading] = useState(!location.state?.empresa)

  useEffect(() => {
    if (!empresa && empresaId) cargarEmpresa()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId])

  const cargarEmpresa = async () => {
    try {
      setLoading(true)
      const r = await asesoriaAPI.getCliente(empresaId)
      setEmpresa(r.data)
    } catch {
      toast.error('Error al cargar empresa cliente')
      navigate('/v2/mis-clientes')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[var(--ink-3)] border-t-transparent" />
      </div>
    )
  }
  if (!empresa) return null

  const nivelAcceso = empresa.nivel_acceso || 'admin'
  const soloLectura = nivelAcceso === 'readonly'
  const empresaExpirada = empresa.fecha_expiracion && new Date(empresa.fecha_expiracion) <= new Date()

  const tabs = [
    { name: 'Empleados',     href: `/asesoria/empresa/${empresaId}/empleados`,      icon: FiUsers },
    { name: 'Grupos',        href: `/asesoria/empresa/${empresaId}/grupos`,         icon: FiLayers },
    { name: 'Calendarios',   href: `/asesoria/empresa/${empresaId}/calendarios`,    icon: FiCalendar },
    { name: 'Centros',       href: `/asesoria/empresa/${empresaId}/centros-trabajo`,icon: FiMapPin },
    { name: 'Tipos pausa',   href: `/asesoria/empresa/${empresaId}/tipos-pausa`,    icon: FiClock },
    { name: 'Fichajes',      href: `/asesoria/empresa/${empresaId}/gestion`,        icon: FiClock },
    { name: 'Reportes',      href: `/asesoria/empresa/${empresaId}/reportes`,       icon: FiBarChart2 },
    { name: 'Auditoría',     href: `/asesoria/empresa/${empresaId}/auditoria`,      icon: FiShield },
    { name: 'Datos empresa', href: `/asesoria/empresa/${empresaId}/configuracion`,  icon: FiBriefcase },
  ]

  const statusLabel = (s) => ({
    trialing: 'Periodo de prueba',
    active: 'Licencia activa',
    expired: 'Licencia expirada',
    canceled: 'Cancelada',
  }[s] || s || '—')

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <div className="border-b border-[var(--line)] bg-[var(--bg)]">
        <div className="max-w-7xl mx-auto px-7 py-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <Btn variant="ghost" size="sm" onClick={() => navigate('/v2/mis-clientes')}>
              <FiArrowLeft size={14} /> Mis clientes
            </Btn>
            <div className="border-l border-[var(--line)] pl-4 min-w-0">
              <div className="eyebrow flex items-center gap-2">
                <span className="font-mono">§ Modo asesoría</span>
                {empresaExpirada && <Pill tone="red">Licencia expirada</Pill>}
                {!empresaExpirada && soloLectura && <Pill tone="amber">Solo lectura</Pill>}
              </div>
              <h1 className="font-display text-[26px] leading-[1.05] text-[var(--ink)] truncate mt-1">
                {empresa.nombre}
              </h1>
              <p className="text-[12px] text-[var(--ink-3)] capitalize">
                Plan {empresa.plan} · {statusLabel(empresa.subscription_status)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-[var(--ink-3)]">
            <FiBriefcase size={12} />
            Asesoría
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-7">
          <nav className="flex gap-1 overflow-x-auto -mb-px">
            {tabs.map((tab) => (
              <NavLink
                key={tab.href}
                to={tab.href}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-3 py-3 text-[13px] border-b-2 whitespace-nowrap transition-colors ${
                    isActive
                      ? 'border-[var(--ink)] text-[var(--ink)] font-medium'
                      : 'border-transparent text-[var(--ink-3)] hover:text-[var(--ink)] hover:border-[var(--line-2)]'
                  }`
                }
              >
                <tab.icon size={14} />
                {tab.name}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {(soloLectura || empresaExpirada) && (
        <div className="max-w-7xl mx-auto px-7 pt-5">
          <div className={`flex items-center gap-3 p-3 rounded-soft text-[13px] ${
            empresaExpirada
              ? 'bg-[rgba(239,68,68,.08)] text-[#C13030] border border-[rgba(239,68,68,.18)]'
              : 'bg-[rgba(245,158,11,.08)] text-[#A86A06] border border-[rgba(245,158,11,.18)]'
          }`}>
            <FiAlertCircle size={14} className="flex-shrink-0" />
            {empresaExpirada
              ? 'La licencia de esta empresa ha expirado. Solo puedes consultar datos.'
              : 'Tienes acceso de solo lectura a esta empresa cliente.'}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-7 py-6">
        <EmpresaGestionProvider empresaId={parseInt(empresaId)} empresa={empresa} tipoGestor="asesoria">
          <Outlet />
        </EmpresaGestionProvider>
      </div>
    </div>
  )
}

export default GestionEmpresaAsesoriaV2
